'use client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BottomScrollListener } from 'react-bottom-scroll-listener';
import { useInfiniteQuery } from '@tanstack/react-query';

import { useTranslations } from 'next-intl';

// import FilterButtonWithIcon from '../../../../../components/FilterButtonWithIcon';
// import FiltersModal from '../../../../../components/FiltersModal';
import Loading from '../../../../../components/Loading';
import LoadingContainer from '../../../../../components/LoadingContainer';
import ResourceItem from '../../../../../components/ResourceItem';
import WarningContainer from '../../../../../components/WarningContainer';
import WarningText from '../../../../../components/WarningText';
import Wrapper from '../../../../../components/Wrapper';
import { amplitudeInstance } from '../../../../../config/amplitude';
import buildListingTitleUrl from '../../../../../helpers/buildListingTitleUrl';
import useFiltersQuery from '../../../../../hooks/useFiltersQuery';
import useIntl from '../../../../../hooks/useIntl';
// import useModal from '../../../../../hooks/useModal';
import { Link } from '../../../../../i18n/navigation';
import api from '../../../../../services/api';
import { BasicResource, Metadata, Person } from '../../../../../types/types';

interface PropTypes {
  person: Person;
}

function ClientPersonTVShows({ person }: PropTypes) {
  const t = useTranslations();
  const intl = useIntl();

  // const {
  //   onOpen: onOpenFiltersModal,
  //   isOpen: isFiltersModalOpen,
  //   onClose: onCloseFiltersModal
  // } = useModal();

  const [filtersQuery, setFiltersQuery] = useFiltersQuery();

  const [totalTVShows, setTotalTVShows] = useState(0);

  const containerRef = useRef<any>(null);

  const tvShowsQuery = useInfiniteQuery<{
    data: BasicResource[];
    metadata: Metadata;
  }>({
    queryKey: [`persons-${person._id}-tv-shows`, filtersQuery.watchProvidersIds],
    queryFn: async ({ pageParam: offset = 0 }) => {
      const response = await api.get(
        `/v1/persons/${person._id}/tv/?offset=${offset || 0}&limit=50`,
        {
          headers: {
            'accept-language': intl.full_language
          },
          params: {
            with_watch_providers: filtersQuery.watchProvidersIds
          }
        }
      );

      return response.data;
    },
    initialPageParam: 0,
    enabled: !!person._id,
    getNextPageParam: lastGroup => {
      if (lastGroup.metadata.total > lastGroup.metadata.offset + 50) {
        return lastGroup.metadata.offset + 50;
      }

      return false;
    }
  });

  useEffect(() => {
    if (tvShowsQuery.isSuccess) {
      // eslint-disable-next-line no-unused-expressions
      tvShowsQuery.data?.pages.forEach(group => {
        setTotalTVShows(prevState => prevState + group.data.length);
      });
    }
  }, [tvShowsQuery.isSuccess, tvShowsQuery.data]);

  const onEndReached = useCallback(() => {
    if (tvShowsQuery.hasNextPage && !tvShowsQuery.isFetchingNextPage) {
      tvShowsQuery.fetchNextPage();
    }
  }, [tvShowsQuery.hasNextPage, tvShowsQuery.isFetchingNextPage]);

  return (
    <>
      {/* <FiltersModal
        isOpen={isFiltersModalOpen}
        onClose={onCloseFiltersModal}
        filters={{ watchProviders: true }}
        onSaveFilters={selectedFilters => {
          if (selectedFilters.watchProvidersIds) {
            tvShowsQuery.remove();
            setFiltersQuery({
              watchProvidersIds: selectedFilters.watchProvidersIds
            });
          }
        }}
      /> */}
      <Wrapper>
        <BottomScrollListener onBottom={onEndReached} offset={150}>
          {() => (
            <div
              ref={containerRef}
              className="flex flex-col w-full bg-gray-600 px-4 md:px-10"
            >
              <div className="flex flex-col bg-gray-600 mt-3 pb-1 w-full z-[8]">
                <div>
                  <h1 className="text-white text-3xl font-bold">
                    {t('persons_tvs.title', {
                      name: person.name
                    })}
                  </h1>
                  <p className="text-gray-500 mt-2">
                    {t('persons_tvs.description', {
                      name: person.name
                    })}
                  </p>
                </div>

                {/* <div className="my-2">
                  <FilterButtonWithIcon onClick={onOpenFiltersModal} />
                </div> */}
              </div>

              <div className="my-2">
                {tvShowsQuery.isLoading && (
                  <LoadingContainer>
                    <Loading />
                  </LoadingContainer>
                )}

                {tvShowsQuery.isSuccess &&
                  tvShowsQuery.data.pages[0].metadata.total === 0 && (
                    <WarningContainer>
                      <WarningText>
                        {t('persons_tvs.warnings.tv_not_found')}
                      </WarningText>
                    </WarningContainer>
                  )}

                {tvShowsQuery.isSuccess && totalTVShows > 0 && (
                  <div className="custom-grid w-full h-full gap-x-6 gap-y-3 pb-4">
                    {tvShowsQuery.data?.pages.map((group, groupIndex) => (
                      <React.Fragment key={groupIndex}>
                        {group.data.map(item => {
                          return (
                            <Link
                              key={item._id}
                              href={buildListingTitleUrl({
                                listingType: 'tv',
                                locale: intl.locale,
                                shortId: item.short_id,
                                slugs: item.slugs
                              })}
                              prefetch={false}
                            >
                              <ResourceItem
                                data={item}
                                onClick={() => {
                                  amplitudeInstance.logEvent(
                                    'Go To Content Page From Person TV Shows Page',
                                    {
                                      content_id: item._id,
                                      content_type: 'tv',
                                      person_id: person._id
                                    }
                                  );
                                }}
                              />
                            </Link>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </BottomScrollListener>
      </Wrapper>
    </>
  );
}

export default ClientPersonTVShows;
